import moment from "moment";
import { isRecord } from "../../../types/internal/utility";
import { type LocalDatabaseSale } from "../../Data/_Bases/BaseSalesRepository";
import { type QueuedItem } from "../../Data/_Bases/BaseUploadRepository";
import { determineDataSource } from "../../Data/DataStores/DataSource";
import NotFoundException from "../../Routes/SellScreen/Exceptions/Data/NotFoundException";
import { UPLOAD_TYPES } from "../../Workers/Upload/UploadEvents";
import UUID from "../Generic/UUID";

const OFFLINE_BACKUP_HANDLE = "offlineBackupHandle";

const FILE_PREFIX = "sale-backup-";
const DIRECTORY_FORMAT = "YYYY-MM-DD";

interface OfflineBackupUpdateHandleEvent {
    type: "UPDATE_HANDLE";
}

type OfflineBackupEvent = OfflineBackupUpdateHandleEvent;

interface OfflineBackupMessage {
    sender: string;
    event: OfflineBackupEvent;
}

interface BackupSale extends Omit<QueuedItem<UPLOAD_TYPES.SALE>, "data"> {
    data: LocalDatabaseSale;
}

function isBackupSale(data: unknown): data is BackupSale {
    if(!isRecord(data)) {
        return false;
    }

    if(!(
        "type" in data &&
        "data" in data
    )) {
        return false;
    }

    return data.type === "sale" &&
        // Hoping the sale data is correct, I don't trust our typings
        isRecord(data.data);
}

export type BackupImportTuple = [
    QueuedItem<UPLOAD_TYPES.SALE>,
    LocalDatabaseSale,
];

export class OfflineSaleBackupManager {
    protected id = UUID.generate();
    protected directoryHandle: FileSystemDirectoryHandle | null = null;
    protected broadcastChannel!: BroadcastChannel;
    protected cleanupInterval: ReturnType<typeof setInterval> | null = null;

    protected constructor() {
        this.handleBroadcastMessage = this.handleBroadcastMessage.bind(this);
        this.loadBackupHandle = this.loadBackupHandle.bind(this);

        if(process.env.NODE_ENV !== "test") {
            // Currently this will hold the Jest process running when testing, if we are testing this properly we should
            // remove this `if` statement and replace with a better implementation
            this.broadcastChannel = new BroadcastChannel("offlineBackupCommunicator");
            this.broadcastChannel.addEventListener("message", this.handleBroadcastMessage);
        }

        void this.loadBackupHandle();
    }

    /**
     * Start running the cleanup timeout
     */
    public scheduleCleanup(): void {
        if(this.cleanupInterval !== null) {
            return;
        }

        void this.cleanBackup();

        // Setup clearing interval
        this.cleanupInterval = setInterval(() => {
            void this.cleanBackup();
        }, 24 * 60 * 60 * 1000); // 24 hours
    }

    /**
     * Stop the cleanup from running
     */
    public stopCleanup(): void {
        if(this.cleanupInterval === null) {
            return;
        }

        clearInterval(this.cleanupInterval);
    }

    /**
     * The message event listener for the offline backup broadcast channel
     * @param message
     * @protected
     */
    protected handleBroadcastMessage(message: MessageEvent<OfflineBackupMessage>): void {
        const { sender, event } = message.data;

        // Do not handle messages sent from this manager
        if(this.id === sender) {
            return;
        }

        if(event.type === "UPDATE_HANDLE") {
            // Attempt to update the handle
            void this.loadBackupHandle();
        }
    }

    /**
     * Broadcasts the message event
     * @param event
     */
    public sendEvent(event: OfflineBackupEvent): void {
        this.broadcastChannel.postMessage({
            sender: this.id,
            event,
        } as OfflineBackupMessage);
    }

    /**
     * Attempt to load the backup handler from IndexedDB
     */
    public async loadBackupHandle(): Promise<FileSystemDirectoryHandle | null> {
        try {
            const source = await determineDataSource();

            this.directoryHandle = await source.system.get<FileSystemDirectoryHandle>(OFFLINE_BACKUP_HANDLE);
        } catch(err) {
            if(err instanceof NotFoundException) {
                this.directoryHandle = null;
            }
        }

        return this.directoryHandle;
    }

    /**
     * Attempt to save the backup handler to IndexedDB
     * @param handle
     */
    public async setBackupHandle(handle: FileSystemDirectoryHandle): Promise<boolean> {
        if(!await this.validatePermission(handle)) {
            return false;
        }

        try {
            const source = await determineDataSource();

            await source.system.put({
                key  : OFFLINE_BACKUP_HANDLE,
                value: handle,
            });

            this.directoryHandle = handle;

            // Ensure all instances (in other tabs and service workers) have updated handle
            this.sendEvent({
                type: "UPDATE_HANDLE",
            });
        } catch(err) {
            console.error("Unable to update backup handle", err);

            return false;
        }

        // Start backup for new location
        void this.exportBackup();

        return true;
    }

    /**
     * Get the sales pending upload
     * @protected
     */
    protected async getPendingSales(): Promise<Array<BackupSale>> {
        const source = await determineDataSource();

        const uploads = await source.uploads.all();

        const results: Array<BackupSale> = [];

        for(const upload of uploads) {
            if(upload.type !== UPLOAD_TYPES.SALE) {
                continue;
            }

            const { data: id, ...item } = upload as QueuedItem<UPLOAD_TYPES.SALE>;

            const sale = await source.sales.get(id);

            results.push({
                ...item,
                data: sale,
            });
        }

        return results;
    }

    /**
     * Export all the sales
     */
    public async exportBackup(): Promise<void> {
        if(!this.directoryHandle) {
            return;
        }

        // Load the database sales
        const sales = await this.getPendingSales();

        // Write out to the database
        for(const sale of sales) {
            await this.backupSale(sale);
        }
    }

    /**
     * Backup a sale upload queue item
     * @param item
     */
    public async backupQueued(item: QueuedItem<UPLOAD_TYPES.SALE>): Promise<void> {
        const source = await determineDataSource();

        const { data: id, ...rest } = item;

        const sale = await source.sales.get(id);

        await this.backupSale({
            ...rest,
            data: sale,
        });
    }

    /**
     * Write the backup sale to the backup file
     * @param sale
     */
    protected async backupSale(sale: BackupSale): Promise<void> {
        if(!this.directoryHandle) {
            return;
        }

        // Check to see if backup is already expired
        const timestamp = moment(sale.data.createdAt);

        if(timestamp.isBefore(moment().subtract(30, "days"))) {
            return;
        }

        // Get or create the directory for the sale
        const directoryName = `${FILE_PREFIX}${timestamp.format(DIRECTORY_FORMAT)}`;
        const timestampDirectory = await this.directoryHandle.getDirectoryHandle(directoryName, {
            create: true,
        });

        // Create the file
        const fileHandle = await timestampDirectory.getFileHandle(`${FILE_PREFIX}${sale.data.id}.json`, {
            create: true,
        });

        const writable = await fileHandle.createWritable();

        await writable.write(JSON.stringify(sale));
        await writable.close();
    }

    /**
     * Cleans up any backup directories that are older than 30 days
     */
    public async cleanBackup(): Promise<void> {
        if(!this.directoryHandle) {
            return;
        }

        if(!await this.validatePermission(this.directoryHandle)) {
            return;
        }

        const cutoff = moment().subtract(30, "days");

        // Check to see which directory should be removed
        const toRemove: Array<string> = [];

        for await (const [ key, item ] of this.directoryHandle.entries()) {
            // Check to see if item is a directory
            if(item.kind !== "directory") {
                continue;
            }

            if(!key.startsWith(FILE_PREFIX)) {
                continue;
            }

            // Check to see if directory is still valid
            const date = moment(key.slice(FILE_PREFIX.length), DIRECTORY_FORMAT);

            if(!date.isBefore(cutoff)) {
                continue;
            }

            toRemove.push(key);
        }

        // Remove the items
        for(const key of toRemove) {
            await this.directoryHandle.removeEntry(key, {
                recursive: true,
            });
        }
    }

    /**
     * Import the backup sales from the directory handle
     * @param handle
     */
    public async importBackup(handle: FileSystemDirectoryHandle): Promise<Array<BackupImportTuple>> {
        const cutoff = moment().subtract(30, "days");

        const results: Array<BackupImportTuple> = [];

        for await (const [ key, item ] of handle.entries()) {
            // Check to see if item is a directory
            if(item.kind !== "directory") {
                continue;
            }

            if(!key.startsWith(FILE_PREFIX)) {
                continue;
            }

            const date = moment(key.slice(FILE_PREFIX.length), "YYYY-MM-DD");

            if(date.isBefore(cutoff)) {
                continue;
            }

            results.push(...await this.processDirectory(item as FileSystemDirectoryHandle));
        }

        return results;
    }

    /**
     * Loop through the files in the directory and get the sales
     * @param handle
     * @protected
     */
    protected async processDirectory(handle: FileSystemDirectoryHandle): Promise<Array<BackupImportTuple>> {
        const results: Array<BackupImportTuple> = [];

        for await (const [ key, item ] of handle.entries()) {
            // Check to see if item is a file
            if(item.kind !== "file") {
                continue;
            }

            if(
                !key.startsWith(FILE_PREFIX) ||
                !key.endsWith(".json")
            ) {
                // Unexpected filename
                continue;
            }

            try {
                const { data, ...rest } = await this.readFile(item as FileSystemFileHandle);

                results.push([
                    {
                        ...rest,
                        data: data.id,
                    },
                    data,
                ]);
            } catch(err) {
                console.error("Error trying to read backup sale", err);
            }
        }

        return results;
    }

    /**
     * Read and parse the file
     * @param handle
     * @protected
     */
    protected async readFile(handle: FileSystemFileHandle): Promise<BackupSale> {
        const file = await handle.getFile();

        const data = JSON.parse(
            await file.text()
        );

        if(!isBackupSale(data)) {
            throw new Error("Unexpected data parsed");
        }

        return data;
    }

    /**
     * Search through the backup directories and delete the file if found
     * @param id
     */
    public async findAndDeleteBackup(id: string): Promise<void> {
        if(!this.directoryHandle) {
            return;
        }

        const needle = `${FILE_PREFIX}${id}.json`;

        for await (const [ key, item ] of this.directoryHandle.entries()) {
            // Check to see if item is a directory
            if(item.kind !== "directory") {
                continue;
            }

            if(!key.startsWith(FILE_PREFIX)) {
                continue;
            }

            // Check to see if the directory has the file
            const directory = item as FileSystemDirectoryHandle;

            for await (const key of directory.keys()) {
                if(key !== needle) {
                    continue;
                }

                // Directory found, delete
                await directory.removeEntry(key);

                return;
            }
        }
    }

    /**
     * Remove the sale from the backup directory
     * @param createdAt
     * @param id
     */
    public async deleteBackup(createdAt: string, id: string): Promise<void> {
        if(!this.directoryHandle) {
            return;
        }

        const date = moment(createdAt).format(DIRECTORY_FORMAT);

        try {
            const directory = await this.directoryHandle.getDirectoryHandle(`${FILE_PREFIX}${date}`, {
                create: false,
            });

            await directory.removeEntry(`${FILE_PREFIX}${id}.json`);
        } catch(err) {
            console.error("Unable to delete backup file", err);
        }
    }

    /**
     * Validate the permission for the file handle
     * @param handle
     * @protected
     */
    protected async validatePermission(handle: FileSystemHandle): Promise<boolean> {
        if((await handle.queryPermission({
            mode: "readwrite",
        })) === "granted") {
            return true;
        }

        return (await handle.requestPermission({
            mode: "readwrite",
        })) === "granted";
    }

    private static instance: OfflineSaleBackupManager;
    public static getInstance(): OfflineSaleBackupManager {
        if(!OfflineSaleBackupManager.instance) {
            OfflineSaleBackupManager.instance = new OfflineSaleBackupManager();
        }

        return OfflineSaleBackupManager.instance;
    }
}
